import gql from 'graphql-tag';
import className from 'classnames/bind';
import { useRef } from 'react';
import { VideoSingularFragment } from '@graphqlTypes';
import { Container, VideoEmbed, SectionHeader, RevealWrapper } from '@components';

import { useViewSize } from '@/providers/ViewSizeContext';
import { useInView } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';

import styles from './VideoSingular.module.scss';
const cx = className.bind(styles);

type VideoSingularProps = Omit<VideoSingularFragment, '__typename'> & {
  detail?: boolean;
};

export default function VideoSingular(props: VideoSingularProps) {
  const {
    headline,
    eyebrow,
    youtubeVideoId,
    videoType,
    videoThumbnail,
    videoUrl,
    youtubeThumbnail,
    videoTitle,
    detail,
    previewVideoUrl,
    cta,
    subhead
  } = props;
  const showHeader = Boolean(eyebrow || headline || subhead);
  const embedData = { videoType, youtubeVideoId, videoUrl, videoThumbnail, youtubeThumbnail, previewVideoUrl };

  // -------------------- Animation

  const rootEl = useRef<HTMLElement | null>(null);
  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });
  const isRevealEnabled = true;

  // -------------------- Render

  return (
    <section className={cx(["video-singular", { detail }])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        <Container className={cx(["video-singular-content"])}>
          { showHeader && (
            <SectionHeader
              eyebrow={eyebrow}
              headline={headline}
              subhead={subhead}
              cta={cta}
              isRevealEnabled={isRevealEnabled}
              isInView={isInView}
            />
          )}
          {
            embedData && <div className={cx("icon-button-hover-container")}><VideoEmbed {...embedData} /></div>
          }
          { videoTitle && (
            <span className={cx(["title-lc-s", "video-title"])}>
              {videoTitle}
            </span>
          )}
        </Container>
      </RevealWrapper>
    </section>
  );

}

VideoSingular.fragments = {
  entry: gql`
    fragment VideoSingular on FlexiblePageContentContentVideoSingularLayout {
      headline
      subhead
      eyebrow
      videoTitle
      videoType
      youtubeVideoId
      videoUrl
      previewVideoUrl
      cta {
        linkType
        externalLink
        label
        page {
          node {
            uri
          }
        }
      }
      videoThumbnail {
        ...ACFMedia
      }
      youtubeThumbnail {
        ...ACFMedia
      }
    }
  `,
};
